import useSWR from "swr";
import { fetcher, api } from "@/lib/api";
import { PageHeader, Section } from "@/components/Heading";
import KpiTile from "@/components/KpiTile";
import { formatIndianCurrency, formatIndianGrouping, formatPct } from "@/lib/format";
import { useEffect, useState } from "react";
import { Star, Sparkles, X, AlertTriangle } from "lucide-react";function Stars({ value }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className="w-3 h-3"
          fill={i <= Math.round(value) ? "currentColor" : "none"} strokeWidth={1.4} />
      ))}
      <span className="font-mono text-[11px] text-muted-foreground ml-1">{value?.toFixed(1)}</span>
    </span>
  );
}

function VendorBriefDrawer({ vendor, onClose }) {
  const [text, setText] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/ai/vendor-brief?vendor_name=${encodeURIComponent(vendor.vendor_name)}`);
      setText(data.text);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vendor.vendor_name]);

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex justify-end" data-testid="vendor-brief-drawer" onClick={onClose}>
      <div className="w-full md:w-[520px] max-w-full bg-background border-l border-border h-full overflow-y-auto flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border sticky top-0 bg-background z-10 px-5 md:px-6 py-4 flex justify-between items-start gap-3">
          <div className="min-w-0">
            <div className="label-caps">Vendor</div>
            <div className="font-display text-xl md:text-2xl mt-1 truncate">{vendor.vendor_name}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 hairline grid place-items-center hover:bg-secondary shrink-0"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-px bg-border">
          {[
            ["Closing value", formatIndianCurrency(vendor.closing_value)],
            ["FY purchased", formatIndianCurrency(vendor.fy_purchased)],
            ["Sell-through", formatPct(vendor.sell_through_pct)],
            ["Fast %", formatPct(vendor.fast_pct)],
            ["Dead %", formatPct(vendor.dead_pct)],
            ["Lead time", `${vendor.lead_time_days}d`],
            ["Quality", `${vendor.quality_score}/5`],
            ["Total SKUs", formatIndianGrouping(vendor.total_skus)],
          ].map(([k, v]) => (
            <div key={k} className="bg-background p-3 min-w-0">
              <div className="label-caps truncate">{k}</div>
              <div className="font-mono text-sm mt-0.5 truncate">{v}</div>
            </div>
          ))}
        </div>
        <div className="px-5 md:px-6 py-6">
          <div className="hairline p-5">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="label-caps text-primary">Negotiation brief · Claude 4.5</span>
              </div>
              <button onClick={load} className="hairline px-3 h-7 text-xs hover:bg-secondary">{loading ? "…" : "Regenerate"}</button>
            </div>
            <p className="text-sm leading-relaxed whitespace-pre-line" data-testid="vendor-brief-text">{loading ? "Drafting brief…" : (text || "Click Regenerate")}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function VendorScorecard() {
  const { data } = useSWR("/stock/vendor-scorecard", fetcher);
  const rows = data?.vendors || [];
  const [open, setOpen] = useState(null);

  // Derived analytics
  const avgSt = rows.length ? rows.reduce((s, v) => s + v.sell_through_pct, 0) / rows.length : 0;
  const avgQ = rows.length ? rows.reduce((s, v) => s + v.quality_score, 0) / rows.length : 0;
  const totalValue = rows.reduce((s, v) => s + v.closing_value, 0);
  const atRisk = rows.filter((v) => v.dead_pct > 30 || v.quality_score < 3.0);
  const best = [...rows].sort((a, b) => b.sell_through_pct - a.sell_through_pct)[0];
  const worst = [...rows].sort((a, b) => b.dead_pct - a.dead_pct)[0];

  return (
    <div>
      <PageHeader
        eyebrow="Stock Intelligence · Vendors"
        title="Scorecard of the 25."
        lede="The vendors that sit at the heart of every margin decision. Sortable, ranked, rated."
      />

      {/* KPI summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border">
        <KpiTile label="Vendors tracked" kind="number" value={rows.length} accent />
        <KpiTile label="Stock under mgmt" value={totalValue} />
        <KpiTile label="Avg sell-through" kind="pct" value={avgSt} />
        <KpiTile label="Avg quality" value={`${avgQ.toFixed(1)} / 5`} kind="text" />
      </div>

      {/* Best vs worst */}
      <div className="grid md:grid-cols-2 gap-px bg-border mt-10">
        <div className="bg-background p-5">
          <div className="label-caps text-positive mb-2">Best performer</div>
          <div className="font-display text-xl">{best?.vendor_name}</div>
          <div className="text-xs text-muted-foreground mt-1">{best?.top_dept}</div>
          <div className="grid grid-cols-3 gap-3 mt-4 font-mono text-xs">
            <div><div className="label-caps">Sell-thru</div><div className="text-positive">{formatPct(best?.sell_through_pct)}</div></div>
            <div><div className="label-caps">Fast %</div><div>{formatPct(best?.fast_pct)}</div></div>
            <div><div className="label-caps">Quality</div><div><Stars value={best?.quality_score || 0} /></div></div>
          </div>
        </div>
        <div className="bg-background p-5">
          <div className="label-caps text-destructive mb-2">Needs attention</div>
          <div className="font-display text-xl">{worst?.vendor_name}</div>
          <div className="text-xs text-muted-foreground mt-1">{worst?.top_dept}</div>
          <div className="grid grid-cols-3 gap-3 mt-4 font-mono text-xs">
            <div><div className="label-caps">Dead %</div><div className="text-destructive">{formatPct(worst?.dead_pct)}</div></div>
            <div><div className="label-caps">Lead</div><div>{worst?.lead_time_days}d</div></div>
            <div><div className="label-caps">Quality</div><div><Stars value={worst?.quality_score || 0} /></div></div>
          </div>
        </div>
      </div>

      {/* At-risk callout */}
      {atRisk.length > 0 && (
        <div className="hairline p-4 flex items-start gap-3 mt-10 bg-secondary/30">
          <AlertTriangle className="w-5 h-5 text-warning mt-0.5 shrink-0" />
          <div className="text-sm">
            <span className="font-display text-base">{atRisk.length} vendors flagged for review</span>
            <span className="text-muted-foreground"> — either holding &gt;30% dead stock or scoring &lt;3.0 on quality. Schedule renegotiation.</span>
          </div>
        </div>
      )}

      <Section eyebrow="The full ranking" title="All vendors">
        <div className="hidden md:block hairline">
          <table className="w-full table-dense">
            <thead>
              <tr>
                <th>Vendor</th>
                <th className="text-right">Closing value</th>
                <th className="text-right">Sell-through</th>
                <th className="text-right">Fast %</th>
                <th className="text-right">Dead %</th>
                <th>Quality</th>
                <th className="text-right">Lead</th>
                <th>Top dept</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v) => (
                  <tr key={v.vendor_name} onClick={() => setOpen(v)} className="cursor-pointer" data-testid="vendor-row">
                  <td className="font-display text-sm cell-truncate-lg" title={v.vendor_name}>{v.vendor_name}</td>
                  <td className="text-right">{formatIndianCurrency(v.closing_value)}</td>
                  <td className="text-right">{formatPct(v.sell_through_pct)}</td>
                  <td className="text-right text-positive">{formatPct(v.fast_pct)}</td>
                  <td className="text-right text-destructive">{formatPct(v.dead_pct)}</td>
                  <td><Stars value={v.quality_score} /></td>
                  <td className="text-right">{v.lead_time_days}d</td>
                  <td className="text-muted-foreground">{v.top_dept}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="md:hidden space-y-2">
          {rows.map((v) => (
            <button key={v.vendor_name} onClick={() => setOpen(v)} className="w-full text-left hairline p-4" data-testid="vendor-card-mobile">
              <div className="font-display text-base truncate" title={v.vendor_name}>{v.vendor_name}</div>
              <div className="grid grid-cols-2 gap-2 mt-2 font-mono text-xs">
                <div><div className="label-caps">Sell-thru</div>{formatPct(v.sell_through_pct)}</div>
                <div><div className="label-caps">Dead %</div>{formatPct(v.dead_pct)}</div>
                <div><div className="label-caps">Lead</div>{v.lead_time_days}d</div>
                <div><div className="label-caps">Quality</div><Stars value={v.quality_score} /></div>
              </div>
            </button>
          ))}
        </div>
      </Section>
      {open && <VendorBriefDrawer vendor={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
